import React, { useState, useEffect, useRef, useContext, useCallback } from "react";

import { useHistory, useParams } from "react-router-dom";
import clsx from "clsx";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import { useThemeContext } from "../../context/DarkMode";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { green } from "@material-ui/core/colors";
import ListItem from "@material-ui/core/ListItem";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import Typography from "@material-ui/core/Typography";
import Avatar from "@material-ui/core/Avatar";
import Divider from "@material-ui/core/Divider";
import Badge from "@material-ui/core/Badge";
import Checkbox from "@material-ui/core/Checkbox";
import RestoreIcon from "@material-ui/icons/Restore";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import { getSocket } from "../../services/socket-io";
import ButtonWithSpinner from "../ButtonWithSpinner";
import TicketPendingBadge from "../TicketPendingBadge";
import { getPending } from "../../services/ticketPendingService";
import MarkdownWrapper from "../MarkdownWrapper";
import { Tooltip, Chip } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SettingsContext } from "../../context/Settings/SettingsContext";
import { fmtSmartTimestamp } from "../../translate/localeHelper";
import { getChipsVisibility } from "../../helpers/getChipsVisibility";
import userFirstName from "../../helpers/userFirstName";
import { formatLastMessage } from "../../helpers/mediaPlaceholder";
import { getContactDisplayName } from "../../helpers/contactDisplayName";
import { useChatHighlight } from "../../context/ChatHighlight/ChatHighlightContext";
import { getHighlightPreset, LEVEL_ACCENT } from "./highlightPresets";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import SelectQueueOnAcceptModal from "../SelectQueueOnAcceptModal";

const useStyles = makeStyles(theme => ({
	ticket: {
		position: "relative",
		// Desktop: checkbox aparece al hover del item; siempre visible si seleccionado o modo activo.
		"&:hover $selectCheckbox": {
			opacity: 1,
		},
	},

	pendingTicket: {
		cursor: "unset",
	},

	contactNameWrapper: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
	},

	lastMessageTime: {
		justifySelf: "flex-end",
		whiteSpace: "nowrap",
		marginLeft: 4,
	},

	closedBadge: {
		alignSelf: "center",
		justifySelf: "flex-end",
		marginRight: 32,
		marginLeft: "auto",
	},

	contactLastMessage: {
		flex: 1,
		minWidth: 0,
		paddingRight: 8,
	},

	newMessagesCount: {
		alignSelf: "center",
		marginLeft: 4,
	},

	badgeStyle: {
		color: "white",
		backgroundColor: green[500],
	},

	acceptButton: {
		backgroundColor: "#25D366",
		color: "#fff",
		height: 24,
		fontSize: "0.7rem",
		fontWeight: 600,
		padding: "0 10px",
		minWidth: "auto",
		textTransform: "none",
		"&:hover": {
			backgroundColor: "#1FAF52",
		},
	},

	ticketQueueColor: {
		flex: "none",
		width: "12px",
		height: "100%",
		position: "absolute",
		top: "0%",
		left: "0%",
	},

	// feat_303: los pending sin departamento llevan la barra izquierda rayada
	// en ámbar. Rayas y no color liso: una cola real puede ser ámbar
	// (co1 "Administracion" = #fcc400), pero ninguna puede ser un gradiente.
	noQueueBar: {
		backgroundImage:
			"repeating-linear-gradient(135deg, #ffb300 0 4px, #ff8f00 4px 8px)",
	},

	aiBadge: {
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		width: 18,
		height: 18,
		borderRadius: "50%",
		fontSize: 9,
		fontWeight: 700,
		marginRight: 4,
		color: "#fff",
	},
	aiBadgeAttending: {
		backgroundColor: "#ef4444",
	},
	aiBadgeDone: {
		backgroundColor: "#22c55e",
	},
	avatarWrapper: {
		position: "relative",
		display: "inline-block",
	},
	// Avatar más grande: aprovecha el alto del zócalo (3 filas) sin agrandar el ítem.
	// feat_328: el tamaño sale de la densidad (fija el alto del zócalo; sin
	// escalarlo la densidad no se percibe en la bandeja).
	bigAvatar: {
		width: (props) => props.avatarSize,
		height: (props) => props.avatarSize,
	},
	selectedItem: {
		backgroundColor: "rgba(37, 211, 102, 0.12)",
	},
	selectCheckbox: {
		padding: 8,
		marginRight: 0,
		opacity: 0,
		transition: "opacity 120ms ease",
		// Touch devices: sin hover real → checkbox siempre visible y tamaño cómodo (>=44x44 touch target con padding 10).
		"@media (hover: none)": {
			opacity: 1,
			padding: 10,
		},
	},
	selectCheckboxVisible: {
		opacity: 1,
	},
	restoreBtn: {
		marginLeft: 4,
	},
	// ChatHighlight v2 — catálogo de intensidades por type (ver highlightPresets.js).
	// NIVEL 1 "soft": pulso lento, color tenue, borde fino.
	"@keyframes hlPulseSoft": {
		"0%": { backgroundColor: "rgba(255,152,0,0.00)" },
		"50%": { backgroundColor: "rgba(255,152,0,0.14)" },
		"100%": { backgroundColor: "rgba(255,152,0,0.00)" },
	},
	hl_soft: {
		animation: "$hlPulseSoft 2.4s ease-in-out infinite",
		boxShadow: "inset -2px 0 0 0 #ff9800",
	},
	// NIVEL 2 "medium": ámbar SATURADO, pulso ~1.3s, borde grueso, alto contraste.
	"@keyframes hlPulseMedium": {
		"0%": { backgroundColor: "rgba(255,111,0,0.04)" },
		"50%": { backgroundColor: "rgba(255,111,0,0.32)" },
		"100%": { backgroundColor: "rgba(255,111,0,0.04)" },
	},
	hl_medium: {
		animation: "$hlPulseMedium 1.3s ease-in-out infinite",
		boxShadow: "inset -5px 0 0 0 #ff6f00",
	},
	// NIVEL 3 "strong": VERDE intenso (oportunidad), pulso ~1s marcado, borde grueso.
	"@keyframes hlPulseStrong": {
		"0%": { backgroundColor: "rgba(0,200,83,0.05)" },
		"50%": { backgroundColor: "rgba(0,200,83,0.36)" },
		"100%": { backgroundColor: "rgba(0,200,83,0.05)" },
	},
	hl_strong: {
		animation: "$hlPulseStrong 1.0s ease-in-out infinite",
		boxShadow: "inset -6px 0 0 0 #00c853",
	},
	// Latido propio del ícono (solo NIVEL 3 lo usa).
	"@keyframes hlIconBeat": {
		"0%": { transform: "scale(1)", opacity: 0.65 },
		"50%": { transform: "scale(1.22)", opacity: 1 },
		"100%": { transform: "scale(1)", opacity: 0.65 },
	},
	highlightIcon: {
		fontSize: 13,
		marginRight: 4,
		flexShrink: 0,
		display: "inline-block",
	},
	iconBeat: {
		animation: "$hlIconBeat 1.0s ease-in-out infinite",
	},
}));

const getTextColor = (hexColor) => {
	if (!hexColor || hexColor.length < 7) return "#ffffff";
	const r = parseInt(hexColor.slice(1, 3), 16);
	const g = parseInt(hexColor.slice(3, 5), 16);
	const b = parseInt(hexColor.slice(5, 7), 16);
	const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
	return luminance > 128 ? "#000000" : "#ffffff";
};

const formatTime = (mins) => {
	if (mins < 1) return "<1m";
	if (mins < 60) return `${mins}m`;
	if (mins < 1440) {
		const h = Math.floor(mins / 60);
		const m = mins % 60;
		return m > 0 ? `${h}h ${m}m` : `${h}h`;
	}
	const d = Math.floor(mins / 1440);
	const h = Math.floor((mins % 1440) / 60);
	return h > 0 ? `${d}d ${h}h` : `${d}d`;
};

// TDR semáforo: umbrales configurables por empresa (con caché localStorage 5min).
// feat_295: el mismo blob cachea attendingMode (una sola lectura de /settings).
const TDR_CACHE_KEY = "waia_tdr_thresholds";
const TDR_CACHE_TTL = 5 * 60 * 1000; // 5 minutos

const getCachedTdrThresholds = () => {
	try {
		const raw = localStorage.getItem(TDR_CACHE_KEY);
		if (!raw) return null;
		const { yellow, red, attendingMode, ts } = JSON.parse(raw);
		if (Date.now() - ts > TDR_CACHE_TTL) return null;
		return { yellow, red, attendingMode: attendingMode || "off" };
	} catch (_) { return null; }
};

const setCachedTdrThresholds = (yellow, red, attendingMode) => {
	try {
		localStorage.setItem(TDR_CACHE_KEY, JSON.stringify({ yellow, red, attendingMode: attendingMode || "off", ts: Date.now() }));
	} catch (_) {}
};

const getTdrColor = (mins, yellowThreshold, redThreshold) => {
	if (mins < yellowThreshold) return "#4caf50";
	if (mins < redThreshold) return "#ff9800";
	return "#f44336";
};

// feat_323 — el chip de status se volvió información de primera línea con la
// bandeja unificada (antes cada status vivía en su propio tab y el chip era
// redundante). Los tres compartían forma y solo cambiaban de color, y el ámbar
// de `pending` choca con los DOS ámbares que ya pinta la fila: el highlight
// soft/medium de ChatHighlight (#ff9800 / #ff6f00, fondo pulsante + borde
// derecho) y la barra izquierda rayada de "pending sin departamento"
// (feat_303, #ffb300/#ff8f00). Tres ámbares con tres significados distintos e
// indistinguibles entre sí.
//
// Se diferencian por FORMA, no por color: `pending` pasa a borde PUNTEADO sin
// relleno (algo pendiente, un hueco por completar) contra el relleno sólido de
// open/closed. La forma sobrevive aunque el ámbar se repita al lado.
// Tamaño/padding se dejan iguales al resto de los chips de la fila.
// ⚠ `labelKey`, no `label`: el texto estaba hardcodeado en español. Se toleraba
// cuando el chip casi no se veía (solo filtro de Estado y papelera); en bandeja
// unificada está en TODAS las filas. Se reusan las keys canónicas que ya nombran
// esos mismos buckets en los tabs, así el chip y el tab dicen lo mismo. La
// resolución va en el RENDER — un i18n.t() acá, a nivel módulo, congelaría el
// idioma en el del import.
const STATUS_LABEL = {
	open: { labelKey: "ticketsList.assignedHeader", color: "#1976d2", variant: "solid" },
	// feat_340: el chip del zócalo usa una key PROPIA — `pendingHeader` la comparte
	// el NOMBRE DEL TAB (TicketsManager) y ese texto no cambia.
	pending: { labelKey: "ticketsList.pendingChipLabel", color: "#f57c00", variant: "dashed" },
	closed: { labelKey: "tickets.tabs.closed.title", color: "#388e3c", variant: "solid" },
};

const statusChipStyle = ({ color, variant }) => ({
	display: "inline-flex",
	alignItems: "center",
	fontSize: 10,
	fontWeight: 600,
	padding: "1px 6px",
	borderRadius: 8,
	flexShrink: 0,
	color,
	...(variant === "dashed"
		? { backgroundColor: "transparent", border: `1.5px dashed ${color}` }
		: { backgroundColor: `${color}22`, border: `1px solid ${color}` }),
});

const TicketListItem = ({
	ticket,
	tdrObjective = 10,
	onAcceptTicket,
	showStatusBadge,
	selectionMode = false,
	isSelected = false,
	onToggleSelect,
	onLongPress,
	onEnterSelection,
	deletedView = false,
	onRestoreSingle,
	onHardDeleteSingle,
	isAdmin = false,
	hasTurnosSession = false,
	turnosExpiresAt = null,
}) => {
	// feat_328 — densidad visual: el avatar escala con la densidad elegida.
	const { cfg } = useThemeContext();
	const classes = useStyles({ avatarSize: cfg?.bigAvatar || 50 });
	const history = useHistory();
	const [loading, setLoading] = useState(false);
	const [now, setNow] = useState(() => Date.now());
	const { ticketId } = useParams();
	const isMounted = useRef(true);
	const { user } = useContext(AuthContext);
	const chipsVisibility = getChipsVisibility(user);
	// feat_315 — tz de la empresa para el timestamp del zócalo. Sin la key (backend
	// viejo o perfil sin acceso) queda undefined → el helper usa la tz del navegador.
	const { getSetting } = useContext(SettingsContext);
	const timezone = getSetting("businessTimezone") || undefined;
	// ChatHighlight: ¿este ticket requiere atención? (resaltado reusable)
	const { getHighlight } = useChatHighlight();
	const highlight = getHighlight(ticket.id);
	// Preset visual según el type del highlight (catálogo de intensidades).
	const highlightPreset = highlight ? getHighlightPreset(highlight.type) : null;
	// feat_091 �� tooltips en chips (desktop only)
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	// feat_340: `title` (texto libre) tiene prioridad sobre `titleKey` (i18n) — lo usa
	// el chip del asesor, que muestra el nombre COMPLETO al hover.
	const ChipWithTooltip = ({ titleKey, title, children }) => {
		if (isMobile) return children;
		return (
			<Tooltip title={title || i18n.t(titleKey)} placement="top" arrow>
				{children}
			</Tooltip>
		);
	};
	const [tdrYellow, setTdrYellow] = useState(5);
	const [tdrRed, setTdrRed] = useState(10);
	// feat_295 — el modo sigue cacheándose acá (blob compartido con los umbrales TDR),
	// pero desde feat_340 el chip del asesor NO depende de él: el candado real de
	// escritura vive en MessageInput + backend, no en el zócalo.
	const [attendingMode, setAttendingMode] = useState("off"); // eslint-disable-line no-unused-vars
	const [ticketPending, setTicketPending] = useState(null);
	const [selectQueueModalOpen, setSelectQueueModalOpen] = useState(false);
	const [localContactState, setLocalContactState] = useState(ticket?.contact?.contactState || null);

	useEffect(() => {
		const cached = getCachedTdrThresholds();
		if (cached) {
			setTdrYellow(cached.yellow);
			setTdrRed(cached.red);
			setAttendingMode(cached.attendingMode);
			return;
		}
		api.get("/settings").then(({ data }) => {
			const find = (key, def) => {
				const s = data.find(s => s.key === key);
				return s ? parseInt(s.value, 10) || def : def;
			};
			const y = find("tdrYellowThreshold", 5);
			const r = find("tdrRedThreshold", 10);
			const amRaw = data.find(s => s.key === "attendingMode");
			const am = amRaw?.value || "off";
			setTdrYellow(y);
			setTdrRed(r);
			setAttendingMode(am);
			setCachedTdrThresholds(y, r, am);
		}).catch(() => {});
	}, []);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		const interval = setInterval(() => setNow(Date.now()), 60000);
		return () => clearInterval(interval);
	}, []);

	useEffect(() => {
		let cancelled = false;
		getPending(ticket.id).then(data => {
			if (!cancelled) setTicketPending(data);
		}).catch(() => {});
		return () => { cancelled = true; };
	}, [ticket.id, ticket._pendingUpdate]);

	// Sync localContactState when ticket prop changes (e.g. full list reload)
	useEffect(() => {
		setLocalContactState(ticket?.contact?.contactState || null);
	}, [ticket?.contact?.contactState]);

	// Real-time socket listener: update pastilla when contact state changes
	useEffect(() => {
		// Usar Number() en ambos lados para evitar type mismatch (string vs number)
		const contactId = Number(ticket?.contact?.id);
		if (!contactId) return;
		// Usar singleton en vez de crear socket nuevo por cada TicketListItem
		const socket = getSocket();
		const handler = (data) => {
			if (data.action === "update" && Number(data.contactId) === contactId) {
				setLocalContactState(data.stateId ? {
					id: data.stateId,
					name: data.stateName,
					color: data.stateColor,
				} : null);
			}
		};
		socket.on("contactState", handler);
		// NO llamar socket.disconnect() — es el singleton compartido
		return () => { socket.off("contactState", handler); };
	}, [ticket?.contact?.id]);

	const handleAcepptTicket = async (id, overrideQueueId) => {
		const userQueues = user?.queues || [];
		const ticketQueueId = ticket.queueId;
		const ticketQueueInUserQueues = userQueues.some(q => q.id === ticketQueueId);

		if (overrideQueueId === undefined && userQueues.length >= 2 && !ticketQueueInUserQueues) {
			setSelectQueueModalOpen(true);
			return;
		}

		const finalQueueId = overrideQueueId !== undefined
			? overrideQueueId
			: (ticketQueueInUserQueues ? ticketQueueId : (userQueues[0]?.id || null));

		setLoading(true);
		try {
			await api.put(`/tickets/${id}`, {
				status: "open",
				userId: user?.id,
				queueId: finalQueueId,
			});
		} catch (err) {
			setLoading(false);
			toastError(err);
			return;
		}
		if (isMounted.current) {
			setLoading(false);
		}
		toast.success(i18n.t("tickets.acceptedSuccess"));
		history.push(`/tickets/${id}`);
		if (onAcceptTicket) onAcceptTicket();
	};

	const handleSelectTicket = id => {
		history.push(`/tickets/${id}`);
	};

	// Fix 5: ojo → navegar al ticket en modo readOnly
	const handlePreview = (e) => {
		e.stopPropagation();
		history.push(`/tickets/${ticket.id}`, { readOnly: true });
	};

	// Fix 1: prefijo del remitente — sin fallback "Asesor"
	const renderSenderPrefix = () => {
		const t = ticket.lastMessageSenderType;
		if (!t) return null;
		if (t === "ai") {
			return (
				<span style={{ fontSize: "0.8em", marginRight: 3, color: "#888", flexShrink: 0 }}>
					🤖 IA ·{" "}
				</span>
			);
		}
		if (t === "advisor") {
			const name = ticket.user?.name;
			return (
				<span style={{ fontSize: "0.8em", marginRight: 3, color: "#888", flexShrink: 0 }}>
					👤{name ? ` ${name} · ` : " "}
				</span>
			);
		}
		if (t === "customer") {
			return (
				<span style={{ fontSize: "0.8em", marginRight: 3, flexShrink: 0 }}>
					👤{" "}
				</span>
			);
		}
		return null;
	};

	// TDR chips — cálculos
	const isClosed = ticket.status === "closed" || ticket.status === "resolved";
	// feat_340 — chip único de asesor: reemplaza al chip de status "En atención"
	// cuando el ticket `open` tiene dueño. Sin gate de attendingMode (D3) y también
	// en MIS tickets (D2).
	const showAdvisorChip = ticket.status === "open" && !!ticket.userId && !!ticket.user?.name;
	const senderType = ticket.lastMessageSenderType;

	// Chip 1: ⏱ TDR — cliente esperando respuesta (lastMessageSenderType === "customer")
	const showTdrWait = !isClosed && senderType === "customer";
	const tdrWaitMins = showTdrWait
		? Math.floor((now - new Date(ticket.updatedAt)) / 60000)
		: null;

	// Chip 2: 💤 — nosotros respondimos, cliente no volvió a escribir
	const showClientSilent = !isClosed && (senderType === "advisor" || senderType === "ai");
	const clientSilentMins = showClientSilent
		? Math.floor((now - new Date(ticket.updatedAt)) / 60000)
		: null;

	// Chip 3: 🕐 Total — siempre visible
	const totalEndMs = isClosed
		? new Date(ticket.resolvedAt || ticket.updatedAt).getTime()
		: now;
	const totalStartMs = ticket.attendedAt
		? new Date(ticket.attendedAt).getTime()
		: new Date(ticket.createdAt).getTime();
	const totalChatMins = Math.floor((totalEndMs - totalStartMs) / 60000);

	// Chips en cerrados: primera respuesta + TDR prom + total
	const firstResponseMins = ticket.firstResponseAt
		? Math.floor((new Date(ticket.firstResponseAt) - new Date(ticket.createdAt)) / 60000)
		: null;
	const tdrPromMins = (ticket.avgResponseTime != null)
		? ticket.avgResponseTime
		: firstResponseMins;
	const closedTotalMins = isClosed
		? Math.floor((new Date(ticket.resolvedAt || ticket.updatedAt) - new Date(ticket.createdAt)) / 60000)
		: null;
	const contactType = ticket.contact?.contactType;
	const contactState = localContactState;

	// feat_303: pending sin departamento → barra izquierda rayada.
	// Gate por la columna plana `queueId` (garantizada en el fetch HTTP y en el
	// push por socket), NO por `ticket.queue` (depende del include del emisor).
	// Solo pending y fuera de la papelera; los grupos SÍ entran.
	const noQueueHighlight =
		!ticket.queueId && ticket.status === "pending" && !deletedView;

	// feat_305 — "Nombre Apellido — Empresa" (apellido/empresa opcionales).
	// isGroup sale del ticket: el include de contact no trae esa columna.
	const contactDisplayName = getContactDisplayName(ticket.contact, {
		isGroup: ticket.isGroup,
	});

	// Para grupos: prefijo "Nombre: " antes del último mensaje
	// Nota: para tipo "customer" no hay nombre individual disponible en el ticket
	// (ticket.contact?.name es el nombre del grupo, no el participante)
	const groupLastSender = ticket.isGroup
		? (ticket.lastMessageSenderType === "ai" ? "🤖 IA"
			: ticket.lastMessageSenderType === "advisor" ? (ticket.user?.name || "Asesor")
			: null)
		: null;

	// Long-press handling for mobile (~500ms)
	const longPressTimerRef = useRef(null);
	const handleTouchStart = useCallback(() => {
		if (!onLongPress) return;
		longPressTimerRef.current = setTimeout(() => {
			onLongPress(ticket.id);
			longPressTimerRef.current = null;
		}, 500);
	}, [onLongPress, ticket.id]);
	const cancelLongPress = useCallback(() => {
		if (longPressTimerRef.current) {
			clearTimeout(longPressTimerRef.current);
			longPressTimerRef.current = null;
		}
	}, []);

	const handleItemClick = (e) => {
		if (selectionMode) {
			e.preventDefault();
			e.stopPropagation();
			if (onToggleSelect) onToggleSelect(ticket.id);
			return;
		}
		// En espera: el click del rectángulo dispara la vista previa (ojo), no abre el chat.
		if (ticket.status === "pending") {
			handlePreview(e);
			return;
		}
		handleSelectTicket(ticket.id);
	};

	const handleCheckboxClick = (e) => {
		// stopPropagation OBLIGATORIO — sin esto el click burbujea al ListItem que navega a /tickets/:id.
		e.stopPropagation();
		if (selectionMode) {
			if (onToggleSelect) onToggleSelect(ticket.id);
		} else {
			// Entrada a selección desde desktop: click del checkbox activa modo + tilda este.
			if (onEnterSelection) onEnterSelection(ticket.id);
		}
	};

	return (
		<>
			<ListItem
				dense
				button
				onClick={handleItemClick}
				onTouchStart={handleTouchStart}
				onTouchEnd={cancelLongPress}
				onTouchMove={cancelLongPress}
				onTouchCancel={cancelLongPress}
				selected={ticketId && +ticketId === ticket.id}
				className={clsx(
					classes.ticket,
					{
						[classes.pendingTicket]: ticket.status === "pending" && !selectionMode,
						[classes.selectedItem]: isSelected,
					},
					highlightPreset && !isSelected ? classes[`hl_${highlightPreset.level}`] : null
				)}
			>
				<Checkbox
					className={clsx(classes.selectCheckbox, {
						[classes.selectCheckboxVisible]: isSelected || selectionMode,
					})}
					size="small"
					color="primary"
					checked={isSelected}
					onClick={handleCheckboxClick}
					inputProps={{ "aria-label": "Seleccionar chat" }}
				/>
				<Tooltip
					arrow
					placement="right"
					title={ticket.queue?.name || i18n.t("ticketsList.noQueue")}
				>
					<span
						style={{
							// feat_303: el inline gana a cualquier clase JSS → cuando manda
							// el gradiente de noQueueBar hay que sacarlo del camino.
							backgroundColor: noQueueHighlight
								? undefined
								: ticket.queue?.color || "#7C7C7C",
						}}
						className={clsx(classes.ticketQueueColor, {
							[classes.noQueueBar]: noQueueHighlight,
						})}
					/>
				</Tooltip>

				{/* feat_340: se sacó el ícono 👁 del avatar. La vista previa sigue viva:
				    el click en una fila `pending` entra en readOnly (handleItemClick). */}
				<ListItemAvatar>
					<div className={classes.avatarWrapper}>
						<Avatar className={classes.bigAvatar} src={ticket?.contact?.profilePicUrl} />
					</div>
				</ListItemAvatar>

				{/* Layout 3 filas */}
				<div style={{ flex: 1, minWidth: 0, paddingLeft: 2 }}>
					{/* Fila 1: nombre + hora */}
					<div className={classes.contactNameWrapper}>
						<Typography
							noWrap
							component="span"
							variant="body2"
							color="textPrimary"
							title={contactDisplayName || undefined}
						>
							{ticket.contact?.status === "pending" && (
								<span style={{ color: "#f59e0b", marginRight: 4, fontSize: 10 }}>●</span>
							)}
							{ticket.contact?.status === "disqualified" && (
								<span style={{ color: "#9ca3af", marginRight: 4, fontSize: 10 }}>●</span>
							)}
							{ticket.isGroup && (
								<span title="Grupo" style={{ marginRight: 4 }}>👥</span>
							)}
							{highlight && highlightPreset && (
								<Tooltip title={i18n.t(highlightPreset.tooltipKey)} placement="top" arrow>
									<span
										className={clsx(classes.highlightIcon, { [classes.iconBeat]: highlightPreset.iconPulse })}
										style={{ color: LEVEL_ACCENT[highlightPreset.level] }}
									>
										{highlightPreset.icon}
									</span>
								</Tooltip>
							)}
							{contactDisplayName}
						</Typography>
						{ticket.status === "closed" && (
							<Badge
								className={classes.closedBadge}
								badgeContent={"closed"}
								color="primary"
							/>
						)}
						<div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
							{ticket.status === "pending" && !deletedView && !selectionMode && (
								<ButtonWithSpinner
									color="primary"
									variant="contained"
									className={classes.acceptButton}
									size="small"
									loading={loading}
									onClick={e => { e.stopPropagation(); handleAcepptTicket(ticket.id); }}
								>
									{i18n.t("ticketsList.buttons.accept")}
								</ButtonWithSpinner>
							)}
							{deletedView && !selectionMode && onRestoreSingle && (
								<Tooltip title={i18n.t("ticketsList.deletedView.restoreOne")} arrow>
									<ButtonWithSpinner
										color="primary"
										variant="outlined"
										className={classes.acceptButton}
										size="small"
										loading={loading}
										onClick={e => { e.stopPropagation(); onRestoreSingle(ticket.id); }}
										startIcon={<RestoreIcon style={{ fontSize: 14 }} />}
									>
										{i18n.t("ticketsList.deletedView.restoreBtn")}
									</ButtonWithSpinner>
								</Tooltip>
							)}
							{deletedView && !selectionMode && isAdmin && onHardDeleteSingle && (
								<Tooltip title={i18n.t("ticketsList.deletedView.hardDeleteOne")} arrow>
									<ButtonWithSpinner
										color="secondary"
										variant="outlined"
										className={classes.acceptButton}
										size="small"
										loading={loading}
										onClick={e => { e.stopPropagation(); onHardDeleteSingle(ticket.id); }}
										startIcon={<DeleteForeverIcon style={{ fontSize: 14 }} />}
										style={{ borderColor: "#f44336", color: "#f44336" }}
									>
										{i18n.t("ticketsList.deletedView.hardDeleteBtn")}
									</ButtonWithSpinner>
								</Tooltip>
							)}
							{ticket.lastMessage && (
								<Typography
									className={classes.lastMessageTime}
									component="span"
									variant="body2"
									color="textSecondary"
								>
									{/* feat_315 — formato tipo WhatsApp (hoy / Ayer / día / DD/MM / DD/MM/YYYY) */}
									{fmtSmartTimestamp(ticket.updatedAt, { timezone })}
								</Typography>
							)}
						</div>
					</div>

					{/* Fila 2: emoji/nombre sender + último mensaje + AI badge + no leídos */}
					<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
						<Typography
							className={classes.contactLastMessage}
							noWrap
							component="span"
							variant="body2"
							color="textSecondary"
						>
							{ticket.isGroup && groupLastSender ? (
								<MarkdownWrapper>{`${groupLastSender}: ${formatLastMessage(ticket.lastMessage || "")}`}</MarkdownWrapper>
							) : (
								<>
									{renderSenderPrefix()}
									{ticket.lastMessage ? (
										<MarkdownWrapper>{formatLastMessage(ticket.lastMessage)}</MarkdownWrapper>
									) : (
										<br />
									)}
								</>
							)}
						</Typography>
						<span style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
							{hasTurnosSession && (() => {
								const mins = turnosExpiresAt
									? Math.max(0, Math.round((new Date(turnosExpiresAt).getTime() - Date.now()) / 60000))
									: null;
								return (
									<span
										className={classes.aiBadge}
										style={{ background: "#00695c", marginRight: 2 }}
										title={i18n.t("turnosChat.badgeActive")}
									>
										⏸ TurnosIA{mins != null ? ` ${mins}min` : ""}
									</span>
								);
							})()}
							{ticket.contact?.aiStatus === "attending" && (
								<span className={clsx(classes.aiBadge, classes.aiBadgeAttending)} title="IA atendiendo">IA</span>
							)}
							{ticket.contact?.aiStatus === "done" && (
								<span className={clsx(classes.aiBadge, classes.aiBadgeDone)} title="IA completada">✓</span>
							)}
							{ticket.contact?.aiBlocked && (
								<Tooltip title="IA bloqueada permanentemente" placement="top">
									<span
										className={classes.aiBadge}
										style={{ background: "#9c27b0", marginLeft: 2 }}
									>
										🚫
									</span>
								</Tooltip>
							)}
							<Badge
								className={classes.newMessagesCount}
								badgeContent={ticket.unreadMessages}
								classes={{ badge: classes.badgeStyle }}
							/>
						</span>
					</div>

					{/* Fila 3: estado de contacto + conexión chip + contactType + tags + semáforo TDR */}
					<div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap", marginTop: 2 }}>
						{chipsVisibility.contactState && contactState && (
							<ChipWithTooltip titleKey="userModal.chipsVisibility.contactState.tooltip">
								<span style={{
									display: "inline-flex", alignItems: "center", gap: 4,
									fontSize: 11, fontWeight: 500, padding: "2px 7px",
									borderRadius: 10, border: `1px solid ${contactState.color}`,
									backgroundColor: contactState.color + "20", color: "#333",
									flexShrink: 0,
								}}>
									<span style={{
										width: 6, height: 6, borderRadius: "50%",
										backgroundColor: contactState.color, flexShrink: 0
									}} />
									{contactState.name}
								</span>
							</ChipWithTooltip>
						)}
						{/* feat_340: un `open` CON asesor no muestra "En atención" — lo reemplaza
						    el chip del nombre de abajo. Sin asesor / pending / closed: igual que antes. */}
						{showStatusBadge && STATUS_LABEL[ticket.status] && !showAdvisorChip && (
							<span style={statusChipStyle(STATUS_LABEL[ticket.status])}>
								{i18n.t(STATUS_LABEL[ticket.status].labelKey)}
							</span>
						)}
						{/* feat_340 (ex feat_295 "ATIENDE X"): UN solo chip con el PRIMER NOMBRE del
						    asesor asignado. Desacoplado de `attendingMode` (es identidad, no candado)
						    y visible también en MIS tickets. Azul #1976d2 = el mismo del status `open`
						    que reemplaza; no compite con el ámbar #e65100 de "⏳ Expira". */}
						{showAdvisorChip && (
							<ChipWithTooltip title={ticket.user.name}>
								<span style={statusChipStyle({ color: "#1976d2", variant: "solid" })}>
									{userFirstName(ticket.user.name)}
								</span>
							</ChipWithTooltip>
						)}
						{/* Fix 3: conexión como chip */}
						{chipsVisibility.whatsappLine && ticket.whatsappId && ticket.whatsapp?.name && (
							<ChipWithTooltip titleKey="userModal.chipsVisibility.whatsappLine.tooltip">
								<Chip
									label={`📱 ${ticket.whatsapp?.name}`}
									size="small"
									style={{
										height: 14,
										fontSize: 9,
										backgroundColor: "#424242",
										color: "#fff",
									}}
								/>
							</ChipWithTooltip>
						)}
						{chipsVisibility.queue && ticket.queue?.name && (
							<ChipWithTooltip titleKey="userModal.chipsVisibility.queue.tooltip">
								<Chip
									label={ticket.queue?.name}
									size="small"
									style={{
										height: 14,
										fontSize: 9,
										backgroundColor: "#757575",
										color: "#fff",
									}}
								/>
							</ChipWithTooltip>
						)}
						{chipsVisibility.contactType && contactType && (
							<ChipWithTooltip titleKey="userModal.chipsVisibility.contactType.tooltip">
								<Chip
									label={contactType.name}
									size="small"
									style={{
										height: 14,
										fontSize: 9,
										backgroundColor: contactType.color || "#666",
										color: getTextColor(contactType.color || "#666"),
									}}
								/>
							</ChipWithTooltip>
						)}
						{chipsVisibility.tags && ticket.tags && ticket.tags.length > 0 && (
							ticket.tags.map(tag => (
								<ChipWithTooltip key={tag.id} titleKey="userModal.chipsVisibility.tags.tooltip">
									<Chip
										label={tag.name}
										size="small"
										style={{ height: 14, fontSize: 9, backgroundColor: tag.color, color: getTextColor(tag.color) }}
									/>
								</ChipWithTooltip>
							))
						)}
						{ticket.contactReason && (
							<Chip
								label={ticket.contactReason}
								size="small"
								style={{ height: 14, fontSize: 9, backgroundColor: "#667eea", color: "#fff" }}
							/>
						)}
						{/* Chip 1: ⏱ TDR — cliente esperando respuesta */}
						{chipsVisibility.attentionTime && showTdrWait && (
							<ChipWithTooltip titleKey="userModal.chipsVisibility.attentionTime.tooltip">
								<span style={{
									display: "inline-flex",
									alignItems: "center",
									fontSize: "0.7em",
									color: "#fff",
									backgroundColor: getTdrColor(tdrWaitMins, tdrYellow, tdrRed),
									borderRadius: 8,
									padding: "1px 5px",
									gap: 2,
									cursor: "default",
								}}>
									⏱ {formatTime(tdrWaitMins)}
								</span>
							</ChipWithTooltip>
						)}
						{/* Chip 2: 💤 — cliente sin responder (feat_340: toggle propio `clientSilence`) */}
						{chipsVisibility.clientSilence && showClientSilent && (
							<ChipWithTooltip titleKey="userModal.chipsVisibility.clientSilence.tooltip">
								<span style={{
									display: "inline-flex",
									alignItems: "center",
									fontSize: "0.7em",
									color: "#fff",
									backgroundColor: "#9e9e9e",
									borderRadius: 8,
									padding: "1px 5px",
									gap: 2,
									cursor: "default",
								}}>
									💤 {formatTime(clientSilentMins)}
								</span>
							</ChipWithTooltip>
						)}
						{/* Chip 3: 🕐 Total — visible en activos (pending/open) */}
						{chipsVisibility.ticketAge && !isClosed && (
							<ChipWithTooltip titleKey="userModal.chipsVisibility.ticketAge.tooltip">
								<span style={{
									display: "inline-flex",
									alignItems: "center",
									fontSize: "0.7em",
									color: "#fff",
									backgroundColor: "#757575",
									borderRadius: 8,
									padding: "1px 5px",
									gap: 2,
									cursor: "default",
								}}>
									🕐 {formatTime(totalChatMins)}
								</span>
							</ChipWithTooltip>
						)}
						<TicketPendingBadge pending={ticketPending} />
						{/* Chip expiryWarning — ticket próximo a expirar */}
						{chipsVisibility.expiration && ticket.expiryWarning && ticket.expiryScheduledAt && (
							<ChipWithTooltip titleKey="userModal.chipsVisibility.expiration.tooltip">
								<span style={{
									display: "inline-flex",
									alignItems: "center",
									fontSize: "0.7em",
									color: "#fff",
									backgroundColor: "#e65100",
									borderRadius: 8,
									padding: "1px 5px",
									gap: 2,
									cursor: "default",
									fontWeight: 600,
								}}>
									⏳ Expira
								</span>
							</ChipWithTooltip>
						)}
						{/* Chips en cerrados: 1ra respuesta + TDR prom + total */}
						{chipsVisibility.attentionTime && isClosed && firstResponseMins !== null && (
							<ChipWithTooltip titleKey="userModal.chipsVisibility.attentionTime.tooltip">
								<span style={{
									display: "inline-flex",
									alignItems: "center",
									fontSize: "0.7em",
									color: "#fff",
									backgroundColor: "#4caf50",
									borderRadius: 8,
									padding: "1px 5px",
									gap: 2,
									cursor: "default",
								}}>
									⚡ 1ra: {formatTime(firstResponseMins)}
								</span>
							</ChipWithTooltip>
						)}
						{chipsVisibility.attentionTime && isClosed && tdrPromMins !== null && (
							<ChipWithTooltip titleKey="userModal.chipsVisibility.attentionTime.tooltip">
								<span style={{
									display: "inline-flex",
									alignItems: "center",
									fontSize: "0.7em",
									color: "#fff",
									backgroundColor: "#2196f3",
									borderRadius: 8,
									padding: "1px 5px",
									gap: 2,
									cursor: "default",
								}}>
									📊 TDR: {formatTime(tdrPromMins)}
								</span>
							</ChipWithTooltip>
						)}
						{chipsVisibility.ticketAge && isClosed && closedTotalMins !== null && (
							<ChipWithTooltip titleKey="userModal.chipsVisibility.ticketAge.tooltip">
								<span style={{
									display: "inline-flex",
									alignItems: "center",
									fontSize: "0.7em",
									color: "#fff",
									backgroundColor: "#757575",
									borderRadius: 8,
									padding: "1px 5px",
									gap: 2,
									cursor: "default",
								}}>
									🕐 {formatTime(closedTotalMins)}
								</span>
							</ChipWithTooltip>
						)}
					</div>
				</div>

			</ListItem>
			<Divider variant="inset" component="li" />
			<SelectQueueOnAcceptModal
				open={selectQueueModalOpen}
				onClose={() => setSelectQueueModalOpen(false)}
				onConfirm={(queueId) => {
					setSelectQueueModalOpen(false);
					handleAcepptTicket(ticket.id, queueId);
				}}
				userQueues={user?.queues || []}
			/>
		</>
	);
};

export default TicketListItem;
